import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { DerivativeSearchProduct } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { catchError, map, of, switchMap, tap, throwError } from 'rxjs';
import { ISSUER_MAPPING } from '../application/issuer-mapping';
import { DerivativeType } from '../entities/derivative.type';
import { DerivativeSearchInfrastructure } from '../infrastructure/derivative-search.infrastructure';
import { DerivativeSearchActions, DerivativeSearchApiActions } from './derivative-search.actions';
import { derivativeSearchFeature, DerivativeSearchState } from './derivative-search.reducer';

@Injectable()
export class DerivativeSearchEffects {
  readonly #actions$ = inject(Actions);
  readonly #infra = inject(DerivativeSearchInfrastructure);
  readonly #store = inject(Store);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public loadProducts$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DerivativeSearchActions.loadProducts),
      concatLatestFrom(() => this.#store.select(derivativeSearchFeature.selectDerivativeSearchState)),
      switchMap(([, state]) =>
        this.#getProductsObservable(state).pipe(
          map((products) => ({ products, highlightedProduct: this.#getHighlightedProduct(products, state.derivativeType) })),
          map(({ products, highlightedProduct }) => DerivativeSearchApiActions.loadProductsSuccess({ products, highlightedProduct })),
          catchError((error: HttpErrorResponse) => of(DerivativeSearchApiActions.loadProductsFailure({ error }))),
        ),
      ),
    );
  });

  public triggerSearch$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        DerivativeSearchActions.setLongOrShort,
        DerivativeSearchActions.setLeverage,
        DerivativeSearchActions.setIsin,
        DerivativeSearchActions.setDerivativeType,
        DerivativeSearchActions.setStrike,
        DerivativeSearchActions.setDuration,
        DerivativeSearchActions.setFactor,
        DerivativeSearchActions.setCap,
        DerivativeSearchActions.setQuickMode,
      ),
      map(() => DerivativeSearchActions.loadProducts()),
    );
  });

  public handleError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(DerivativeSearchApiActions.loadProductsFailure),
        tap((error) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  #getProductsObservable(state: DerivativeSearchState) {
    const { isin, cap, derivativeType, duration, factor, leverage, productType, quickMode: quickmode, strike } = state;
    switch (derivativeType) {
      case 'discount':
        return this.#infra.getDiscounts(isin, cap, duration);
      case 'knockout':
        return this.#infra.getKnockOuts(isin, productType, leverage);
      case 'factor':
        return this.#infra.getFactors(isin, productType, factor, quickmode);
      case 'optionsscheine':
        return this.#infra.getOS(isin, productType, strike, duration);
      default:
        return throwError(() => new Error('Derivative type not supported'));
    }
  }

  // logic here: GRAT-7465 - Goldman Sachs Ad
  // UPDATE: GRAT-8889 - UBS Ad
  #getHighlightedProduct(products: DerivativeSearchProduct[], type: DerivativeType) {
    return environment.derivativeHighlightedProductAd && products?.length && type === 'knockout'
      ? products.find(({ emittent }) => ISSUER_MAPPING[emittent] === environment.derivativeHighlightedProductAd)
      : null;
  }
}
